// Moving a job on the calendar should move the crew with it.
//
// Two shapes of change, and they mean different things to the people on the job:
//
//   SLIDE    start and end move by the same amount — the whole job shifted.
//            Everyone shifts with it and keeps the length of their own stint.
//
//   RESHAPE  only one edge moved: the job got longer, shorter, or now starts
//            later but still finishes the same day. Here only the people whose
//            dates were pinned to the edge that moved should follow. A Finish
//            Supt who already rolls off six weeks before completion shouldn't
//            gain six weeks just because completion slipped.
//
// People with no dates of their own already inherit the job's window (the Gantt
// and utilization both fall back to the project dates), so they follow for free
// and need no write at all.

const DAY = 86400000;

export const ymd = (d) => (d ? String(d).slice(0, 10) : "");

const utc = (d) => new Date(ymd(d) + "T00:00:00Z");

export const addDays = (d, n) => (d ? new Date(utc(d).getTime() + n * DAY).toISOString().slice(0, 10) : "");

export const dayDiff = (a, b) => (a && b ? Math.round((utc(b) - utc(a)) / DAY) : 0);

// Calendar months, so "push a month" from the 1st lands on the 1st. A day that
// doesn't exist in the target month (the 31st of a 30-day month) clamps back to
// the last day rather than spilling into the month after.
export function addMonths(d, n) {
  if (!d) return "";
  const x = utc(d);
  const day = x.getUTCDate();
  const t = new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + n, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(day, last));
  return t.toISOString().slice(0, 10);
}

// "35 days later (5 weeks)" reads better than a bare day count when someone is
// pushing a job by a month.
export function describeShift(days) {
  if (!days) return "no change";
  const n = Math.abs(days);
  const dir = days > 0 ? "later" : "earlier";
  const weeks = n % 7 === 0 ? n / 7 : null;
  const unit = `${n} ${n === 1 ? "day" : "days"}`;
  if (weeks && weeks > 1) return `${unit} ${dir} (${weeks} weeks)`;
  return `${unit} ${dir}`;
}

export const shortMDY = (d) => {
  const p = ymd(d).split("-");
  return p.length === 3 ? `${+p[1]}/${+p[2]}/${p[0].slice(2)}` : ymd(d);
};

// Work out what should happen to each person when a job's dates change.
// Returns { startShift, endShift, slide, rows } — rows carry both the current
// and proposed dates so the wizard can show the before/after side by side.
export function planCrewMove({ oldStart, oldEnd, newStart, newEnd, members }) {
  const os = ymd(oldStart), oe = ymd(oldEnd), ns = ymd(newStart), ne = ymd(newEnd);
  const startShift = dayDiff(os, ns);
  const endShift = dayDiff(oe, ne);
  const slide = startShift !== 0 && startShift === endShift;

  const rows = (members || []).map((m) => {
    const curStart = ymd(m.StartDate);
    const curEnd = ymd(m.EndDate);
    // no dates of their own — they already track the job
    const follows = !curStart && !curEnd;

    let nextStart = curStart;
    let nextEnd = curEnd;

    if (!follows) {
      if (slide) {
        if (curStart) nextStart = addDays(curStart, startShift);
        if (curEnd) nextEnd = addDays(curEnd, endShift);
      } else {
        if (curStart && os && ns && curStart <= os) nextStart = ns;
        if (curEnd && oe && ne && curEnd >= oe) nextEnd = ne;
      }
      // never leave someone finishing before they start
      if (nextStart && nextEnd && nextStart > nextEnd) nextEnd = nextStart;
    }

    return {
      id: m.Id,
      name: m.PersonName,
      role: m.ProjectRole || "",
      follows,
      curStart, curEnd,
      nextStart, nextEnd,
      changed: !follows && (nextStart !== curStart || nextEnd !== curEnd),
    };
  });

  return { startShift, endShift, slide, rows };
}
